/* eslint-disable jsx-a11y/label-has-associated-control */
import { PageProps } from 'gatsby';
import React, { FC, useContext } from 'react';
import { FormProvider } from 'react-hook-form';
import styled, { css } from 'styled-components';

import Blade from './Blade';
import { ListingLoader } from './ListingLoader';
import { Notice } from './Notice';
import { Pagination } from './Pagination';
import { Section } from './Section';
import { StickyViewContainer } from './StickyViewContainer';
import { CreatePropertyAlertBlade } from './my-kfh/CreatePropertyAlertBlade';
import { CreatePropertyAlertLoginOrRegisterBlade } from './my-kfh/CreatePropertyAlertLoginOrRegisterBlade';
import { RegisterBlade } from './my-kfh/RegisterBlade';
import { SimplePageHeader } from './pageHeader/SimplePageHeader';
import { PropertiesError } from './properties/PropertiesError';
import { PropertiesNoResults } from './properties/PropertiesNoResults';
import { PropertyCard } from './properties/PropertyCard';
import { StyledPropertyGrid } from './properties/PropertyListing';
import { PropertyListingBanner } from './properties/PropertyListingBanner';
import { PropertyMap } from './properties/PropertyMap';
import { RecentlyViewedProperties } from './properties/RecentlyViewedProperties';
import { RequestAValuationBlade } from './properties/RequestAValuationBlade';
import { SearchActions } from './properties/SearchActions';
import { PropertySearch } from './search/PropertySearch';
import { PropertySearchCommercial } from './search/PropertySearchCommercial';
import { SearchFacets } from './search/SearchFacets';
import { SearchFacetsCommercial } from './search/SearchFacetsComercial';
import { LISTINGS } from '../const';
import { ExperimentsContext } from '../context/ExperimentsContext';
import { MapListingContext, MapListingContextProvider } from '../context/MapListingContext';
import { UiStateContext } from '../context/UiStateContext';
import { UseBuildInfo } from '../hooks/useBuildInfo';
import { useHasMounted } from '../hooks/useHasMounted';
import { useSearch } from '../hooks/useSearch';
import { SearchCategory, TypeOfSearch } from '../types/enums';
import {
  Kfh_Properties,
  Kfh_SearchFacetType,
  MyKfhCreateSavedSearchInput,
  SearchPropertiesQueryVariables,
} from '../types/graphqlTypes';
import { isBrowser } from '../utils/isBrowser';
import { createQueryString, getQueryString } from '../utils/queryStrings';
import { useSeoPropertyData } from '../hooks/useSeoPropertyData';
import { SEO } from './Seo';
import { CTACard } from './CtaCard';

// interface SearchResultsPageProps {
//   site: {
//     buildTime: Date;
//   };
// }
interface SearchResultsPageContextProps {
  lastBuild: Date;
}
interface SearchResultsProps {
  properties: Kfh_Properties;
  query: Partial<SearchPropertiesQueryVariables>;
  pagePath: string;
  error?: any;
}
interface SearchLocationProps {
  type?: string;
  category?: string;
  searchtext?: string;
  period?: string;
  price?: string;
  pricemax?: string;
  pricer?: string;
  pricermax?: string;
  bedrooms?: string;
  bedroomsmax?: string;
  let?: string;
  kfh?: string;
  furnish?: string;
  currencyid?: string;
  newhomes?: string;
  underoffer?: string;
  postcode?: string;
  location?: string;
  s?: string;
  page?: string;
  propertyType?: string;
  propertyStyle?: string;
  searchFirstRunDefault?: boolean;
}

export const StyledValuationBanner = styled(CTACard)`
  ${({ theme: { media, maxMedia } }) => css`
    text-align: left;
    text-wrap: pretty;

    > div {
      flex-grow: 0;
    }

    /* @media (min-width: ${media.lg}) and (max-width: ${maxMedia.lg}) {
      grid-column: 1 / 3;
    } */

    @media (min-width: ${media.lg}) {
      text-align: center;
    }
  `};
`;

const SearchResultsContent = ({
  location,
  serverData,
  pageContext,
  globalLoading,
}: PageProps<unknown, SearchResultsPageContextProps, SearchLocationProps, SearchResultsProps>) => {
  // Set the search category for filters
  const queryStringParsed = location?.search ? getQueryString(location.search) : undefined;
  const category = serverData?.query?.category ?? queryStringParsed?.category ?? SearchCategory.Resale;
  const type = serverData?.query?.type ?? queryStringParsed?.type ?? TypeOfSearch.Residential;
  const { activeBlade, setActiveBlade } = useContext(UiStateContext);
  const { experiments } = useContext(ExperimentsContext);
  const { activeView } = useContext(MapListingContext);
  const hasMounted = useHasMounted();
  const buildInfo = UseBuildInfo();
  const unavailable = serverData?.query?.unavailable ?? queryStringParsed?.unavailable ?? false;

  const searchUnavailableType = () => {
    if (category === 'RESALE' && unavailable === 'true') {
      return 'SOLD';
    }

    if (category === 'RENTAL' && unavailable === 'true') {
      return 'LET';
    }
    return 'DEFAULT';
  };

  const disableAutoSearch = false;
  const isServerSide = !!serverData?.pageNumber;

  // Hook to setup a search results page
  const overrideValues: any = { category, type };
  if (serverData?.query?.page) {
    overrideValues.page = +serverData.query.page;
  }
  const {
    searchMeta,
    loading: searchLoading,
    called,
    error,
    data: propertySearchResultsClient,
    handleOnSubmit,
    formMethods,
    handleReset,
    searchValues,
    activeFiltersCount,
    activeFilters,
    queryString,
    page,
  } = useSearch({
    queryString: serverData?.query ? createQueryString(serverData?.query) : location.search,
    overrideValues,
    activeView,
    lastBuild: buildInfo?.lastBuild,
    disableAutoSearch,
    searchFirstRunDefault: location?.state?.searchFirstRunDefault,
  });

  const loading = searchLoading || globalLoading;

  // Form methods used on this page ( all passed to FOrm Context)
  const { setValue, watch } = formMethods;

  const handleChangePage = (pageNumber: number) => {
    setValue('page', pageNumber, { shouldDirty: true });
  };
  const generatePageUrl = (pageNum: number): string => `${serverData?.pagePath}${pageNum > 1 ? `${pageNum}` : ''}`;

  const pagingProps: Record<string, any> = {};
  if (serverData?.pagePath) {
    pagingProps.linkGenerator = generatePageUrl;
  } else {
    pagingProps.onPageChange = handleChangePage;
  }

  // Data from server or live query
  const propertySearchResults = propertySearchResultsClient?.properties || serverData?.properties;
  const propertySearchQuery = serverData?.query;

  // Total number of returned results
  const totalCount = propertySearchResults?.propertySearch?.properties?.totalCount || 0;

  const multiSearch = propertySearchResults?.propertySearch?.multiSearch
    ? {
        multiSearch: propertySearchResults.propertySearch.multiSearch.multiSearch,
        name: propertySearchResults.propertySearch.multiSearch.displayName,
      }
    : undefined;

  const propertyCount = `${totalCount} ${totalCount === 1 ? `property` : ` properties`} found`;

  const urlFallBack = isBrowser ? `${window.location.pathname}${window.location.search}` : '';
  const savedSearchDefault: MyKfhCreateSavedSearchInput = {
    alertSettings: [
      {
        deliveryFrequency: 'Weekly',
        deliveryDay: 'Monday',
        deliveryTime: '09:00',
      },
    ],
    apiPath: propertySearchResults?.propertySearch?.propertyEndpointApi,
    name: propertySearchResults?.propertySearch?.seo?.h1,
    url: isBrowser && queryString ? `${window.location.pathname}?${queryString}` : urlFallBack,
    payload: {
      locations: propertySearchResults?.propertySearch?.multiSearch
        ? propertySearchResults?.propertySearch?.multiSearch?.displayName
        : 'london',
      category: category || '',
      instantAlert: true,
    },
  };

  const onlynewhomes = watch('onlynewhomes');
  const showSearch = ((!loading && called) || isServerSide) && hasMounted;

  const linkItems = [
    { text: 'Mortgages and Insurance', url: '/services/financial-services/', state: { fromSearchResults: true } },
    { text: 'Property Solicitors', url: '/services/property-solicitors/', state: { fromSearchResults: true } },
    {
      text: 'Survey and Professional Services',
      url: '/services/surveying-and-professional-services/',
      state: { fromSearchResults: true },
    },
    { text: 'See all our services', url: '/services', state: { fromSearchResults: true } },
  ];

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleOnSubmit}>
          {/* Commercial Search */}
          {queryStringParsed?.type === TypeOfSearch.Commercial && (
            <PropertySearchCommercial
              parameters={searchMeta}
              category={category}
              facets={propertySearchResults?.propertySearch?.searchFacets as Kfh_SearchFacetType}
              isLoading={loading}
              suppressHydrationWarning
            />
          )}
          {/* Residential Search */}
          {queryStringParsed?.type !== TypeOfSearch.Commercial && (
            <PropertySearch
              parameters={searchMeta}
              facets={propertySearchResults?.propertySearch?.searchFacets as Kfh_SearchFacetType}
              category={category as SearchCategory}
              multiSearch={multiSearch}
              activeFiltersCount={activeFiltersCount}
              isLoading={loading}
              suppressHydrationWarning
            />
          )}
          <Blade
            id="propertyFilters"
            heading="Filters"
            primaryButton={{
              label: 'Apply filters',
              disableClickTracking: true,
              onClick: (e) => {
                handleOnSubmit(e);
                setActiveBlade(null);
              },
            }}
            secondaryButton={{
              label: 'Clear filters',
              onClick: () => {
                handleReset();
                setActiveBlade(null);
              },
            }}
          >
            {queryStringParsed?.type !== TypeOfSearch.Commercial && (
              <SearchFacets
                facets={propertySearchResults?.propertySearch?.searchFacets as Kfh_SearchFacetType}
                searchType={queryStringParsed?.type as TypeOfSearch}
                category={category as SearchCategory}
                parameters={searchMeta}
                loading={loading}
                multiSearch={multiSearch}
                activeFilters={activeFilters}
              />
            )}

            {queryStringParsed?.type === TypeOfSearch.Commercial && (
              <SearchFacetsCommercial
                facets={propertySearchResults?.propertySearch?.searchFacets as Kfh_SearchFacetType}
                parameters={searchMeta}
                loading={loading}
              />
            )}
          </Blade>

          <>
            <>
              {!isServerSide && !called && loading ? (
                <SimplePageHeader title="Searching" meta="" tight />
              ) : (
                <SimplePageHeader
                  title={propertySearchResults?.propertySearch?.seo?.h1 || `Search results`}
                  meta={
                    propertySearchResults?.propertySearch?.fallBackSearch.fallbackSearch
                      ? `0 properties found`
                      : propertyCount
                  }
                  tight
                />
              )}
              <SearchActions
                searchOrderType={searchUnavailableType()}
                disable={totalCount === 0}
                savedSearch={savedSearchDefault}
                disableAlerts={onlynewhomes}
              />

              <StickyViewContainer>
                <>
                  {activeView === 'list' && (
                    <>
                      <Section contained background="grey">
                        {!propertySearchResults && loading && <ListingLoader />}
                        {!loading && error && <PropertiesError error={error} />}
                        {!loading && !error && called && totalCount === 0 && <PropertiesNoResults />}
                        {propertySearchResults &&
                          propertySearchResults?.propertySearch?.properties &&
                          totalCount >= 1 && (
                            <StyledPropertyGrid>
                              {propertySearchResults?.propertySearch?.fallBackSearch.fallbackSearch && (
                                <Notice
                                  heading="Sorry, we couldn't find any properties in this area."
                                  text={`${propertySearchResults?.propertySearch?.fallBackSearch.fallbackSearchReason}.`}
                                />
                              )}
                              {propertySearchResults?.propertySearch?.properties.edges?.map(
                                (property, index: number) => {
                                  if (!property) {
                                    return null;
                                  }

                                  // Check for a property banner

                                  const propertyBanners = propertySearchResults?.propertySearch?.banners;
                                  const currentPosition = index + 1;

                                  let banners = propertyBanners;

                                  if (experiments?.KFH24008 === 'variation') {
                                    banners = propertyBanners.map((banner) => {
                                      if (banner.id === '3') {
                                        return { ...banner, position: 9 };
                                      }

                                      return banner;
                                    });
                                  }

                                  const currentBanner = banners?.filter(
                                    (propertyBanner) => propertyBanner.position === currentPosition
                                  )[0];

                                  return (
                                    <React.Fragment key={`results-${index}-${property?.node?.id}`}>
                                      {currentBanner && page === 1 && (
                                        <PropertyListingBanner banner={currentBanner.banner} />
                                      )}
                                      <PropertyCard
                                        key={`property-card-${property?.node?.id}`}
                                        property={property.node}
                                        as="li"
                                        clickSection="property search results"
                                      />
                                      {experiments?.KFH25001 === 'variation' &&
                                      category === 'RESALE' &&
                                      (currentPosition === 1 ||
                                        (totalCount === 3 && currentPosition === 1) ||
                                        (totalCount === 2 && currentPosition === 1) ||
                                        (totalCount === 1 && currentPosition === 1)) &&
                                      page === 1 ? (
                                        <StyledValuationBanner
                                          title="Specialist services at every stage of your property journey"
                                          backgroundColour="brand"
                                          tightSpacing
                                          linkItems={linkItems}
                                        />
                                      ) : (
                                        ''
                                      )}
                                    </React.Fragment>
                                  );
                                }
                              )}
                              {!loading && (!propertySearchResults || !propertySearchResults?.propertySearch) && (
                                <PropertiesNoResults />
                              )}
                            </StyledPropertyGrid>
                          )}
                      </Section>
                      {totalCount > LISTINGS.perPage && (
                        <Section contained>
                          <Pagination
                            {...pagingProps}
                            totalCount={totalCount}
                            currentPage={serverData?.query?.page || page}
                            pageSize={LISTINGS.perPage}
                          />
                        </Section>
                      )}
                      {showSearch ? (
                        <RecentlyViewedProperties
                          divider={totalCount > LISTINGS.perPage}
                          usedIn="recently viewed carousel search results"
                        />
                      ) : (
                        ''
                      )}
                    </>
                  )}

                  {activeView === 'map' && (
                    <Section contained background="white">
                      <PropertyMap search={searchValues} />
                    </Section>
                  )}
                </>
              </StickyViewContainer>
            </>
          </>
        </form>
      </FormProvider>

      {/* Create a property alert */}
      {activeBlade === 'createPropertyAlertLoginOrRegister' && <CreatePropertyAlertLoginOrRegisterBlade />}
      {activeBlade === 'createPropertyAlert' && (
        <CreatePropertyAlertBlade
          apiPath={propertySearchResults?.propertySearch?.propertyEndpointApi}
          defaultName={propertySearchResults?.propertySearch?.seo?.h1}
          locations={
            propertySearchResults?.propertySearch?.multiSearch
              ? propertySearchResults?.propertySearch?.multiSearch?.displayName
              : 'london'
          }
          category={propertySearchQuery?.category}
        />
      )}

      {/* Register */}
      <RegisterBlade />

      {/* Valuation */}
      <RequestAValuationBlade />
    </>
  );
};

export const SearchResults: FC<PageProps<unknown, unknown, SearchLocationProps, SearchResultsProps>> = (props) => (
  // eslint-disable-next-line react/destructuring-assignment
  <MapListingContextProvider defaultActiveView={props.location?.state?.activeView}>
    <SearchResultsContent {...props} />
  </MapListingContextProvider>
);
